import './BubbleSearchSelect.scss';
import React, { useState, useEffect, useRef } from 'react';
import { BubbleDebounce, BubbleInput } from '@go-client/components/BubbleUI/index';

interface BubbleSearchSelectPropsBase<T> {
  variant?: 'navy' | 'forest' | 'velvet' | 'titanium';
  appearance?: 'bare' | 'rounded';
  block?: boolean;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  menuClassName?: string;
  suggestionClassName?: string;

  // Function used to get results from an async method
  fetchOptions: (query: string) => Promise<T[]> | T[];
  // Function used to render items
  renderItem: (item: T) => React.ReactNode;
  // Function used to control the value from the item
  getItemValue: (item: T) => string;
  // Event fired when an item is selected
  onItemSelected: (item: T | undefined) => void;
  placeholder?: string;
  suggestionsText?: string;

  minLength?: number;
}

export type BubbleSearchSelectProps<T> = React.HTMLAttributes<HTMLDivElement> &
  BubbleSearchSelectPropsBase<T>;

export function BubbleSearchSelect<T>(props: BubbleSearchSelectProps<T>) {
  const {
    variant,
    appearance,
    block,
    suggestionsText,
    inputClassName: propInputClassName,
    inputStyle: propInputStyle,
    menuClassName: propMenuClassName,
    suggestionClassName: propSuggestionClassName,

    // from div element itself
    className: propClassName,
    style: propStyle,

    fetchOptions,
    renderItem,
    getItemValue,
    onItemSelected,
    placeholder = 'Type to search...',
    minLength = 3,
    ...rest
  } = props;
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [lastResults, setLastResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Remove options when clicking outside the container
        setResults([]);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (query.length < minLength) {
      setResults([]);
      onItemSelected(undefined);
      return;
    }

    let isCancelled = false;

    setLoading(true);
    const options = fetchOptions(query);

    if (options && typeof (options as Promise<T[]>).then === 'function') {
      (options as Promise<T[]>).then(res => {
        if (!isCancelled) {
          setResults(res);
          setLastResults(res);
          setLoading(false);
        }
      });
    } else {
      setResults(options as T[]);
      setLastResults(options as T[]);
      setLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [query]);

  const handleSelect = (item: T) => {
    onItemSelected(item);
    setSelectedValue(getItemValue(item));
    setResults([]);
  };

  const onFocusHandler = () => {
    if (query && lastResults) {
      setResults(lastResults);
    }
  };

  const classes = ['bubble-typeahead'];
  if (block) classes.push('full');
  if (appearance) classes.push(appearance);
  if (variant) classes.push(variant);
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const inputClasses = ['bt-input', 'w-full', 'bubble-search-select'];
  if (propInputClassName) inputClasses.push(propInputClassName);

  let inputStyle: React.CSSProperties = {};
  if (propInputStyle) inputStyle = { ...inputStyle, ...propInputStyle };

  const menuClasses = ['bubble-suggestions'];
  if (propMenuClassName) menuClasses.push(propMenuClassName);

  const suggestionClasses = ['suggestions-item', 's-tab'];
  if (propSuggestionClassName) suggestionClasses.push(propSuggestionClassName);

  const showNoSuggestionsInfo = !lastResults.length;

  return (
    <div ref={containerRef} {...rest} className={classes.join(' ')} style={style}>
      <BubbleDebounce
        onTrigger={[
          (search: React.ChangeEvent<HTMLInputElement>) => setQuery(search.target.value),
          500,
        ]}
      >
        {({ debounce }) => (
          <>
            <BubbleInput
              type="search"
              variant={variant}
              block
              value={selectedValue}
              className={'w-full'}
              inputClassName={inputClasses.join(' ')}
              style={inputStyle}
              onValue={debounce}
              placeholder={placeholder}
              onFocus={onFocusHandler}
              showSpinner={loading}
            />
          </>
        )}
      </BubbleDebounce>

      {!loading && query && (
        <div className={menuClasses.join(' ')}>
          {results.length > 0 &&
            results.map((item, idx) => (
              <div key={idx} onClick={() => handleSelect(item)} className={`suggestions-option`}>
                <div className={suggestionClasses.join(' ')} tabIndex={-1}>
                  {renderItem(item)}
                </div>
              </div>
            ))}

          {showNoSuggestionsInfo && (
            <div className={`suggestions-option`}>
              <div className="suggestions-empty">{suggestionsText || 'No suggestions to show'}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
